import { SingleImage } from "@faire/deprecated-retailer-visitor-shared/features/Showroom/ApparelCategory/SingleImage";
import { Category } from "@faire/deprecated-retailer-visitor-shared/models/Category/Category";
import {
  useMilestone,
  useRecordMilestone,
} from "@faire/deprecated-retailer-visitor-shared/stores/domain/useMilestone";
import { Core } from "@faire/design-tokens";
import { Close } from "@faire/slate/icons/Close";
import trackClick from "@faire/web-api--source/endpoints/www/api/click-track/post";
import { trackApparelDiscoveryShowroomV2Click } from "@faire/web-api--source/events/apparelDiscovery/click/showroomV2";
import { trackApparelDiscoveryShowroomV2View } from "@faire/web-api--source/events/apparelDiscovery/view/showroomV2";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { SHOWROOM_APPAREL_C2_TITLE_BANNER } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/SHOWROOM_APPAREL_C2_TITLE_BANNER";
import { SHOWROOM_APPAREL_C2_TITLE_BANNER_DISMISSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/SHOWROOM_APPAREL_C2_TITLE_BANNER_DISMISSED";
import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { useCallback, useState } from "react";
import styled, { css } from "styled-components";

interface IProps {
  layoutElement: ILayoutElement;
  category?: Category;
  className?: string;
  requestId?: string;
  isLoggedOutPage?: boolean;
}

const IMAGE_GAP = 4;
const LEFT_IMAGE = { width: 144, height: 192 };
const RIGHT_IMAGE = { width: 156, height: 192 };

export const ShowroomTitleBanner: React.FC<IProps> = ({
  layoutElement,
  className,
  isLoggedOutPage,
  requestId,
  category,
}) => {
  const { isMobile } = useViewport();
  const {
    hasOccurred: hasShowroomApparelC2TitleBannerDismissed,
    incrementCount: recordShowroomApparelC2TitleBannerDismissed,
  } = useMilestone(SHOWROOM_APPAREL_C2_TITLE_BANNER_DISMISSED);

  const recordMilestone = useRecordMilestone();

  const [expectedContainerHeight, setExpectedContainerHeight] =
    useState<number>(200);

  const eventName = `${layoutElement.type}-${category?.mostSpecificPathIdentifier}`;

  const measuredRef = useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      setExpectedContainerHeight(node.clientHeight);
    }
  }, []);

  const onVisible = () => {
    recordMilestone(SHOWROOM_APPAREL_C2_TITLE_BANNER);
    trackApparelDiscoveryShowroomV2View(eventName, requestId ?? "", "");
  };

  const ref = useDoActionWhenVisible(onVisible);

  const onClose = () => {
    recordShowroomApparelC2TitleBannerDismissed();
    trackApparelDiscoveryShowroomV2Click(
      eventName,
      "dismissed",
      requestId ?? ""
    );
  };

  const onClick = () => {
    const linkToCategoryPathId = layoutElement.category_path_identifier;
    trackApparelDiscoveryShowroomV2Click(
      eventName,
      linkToCategoryPathId ?? "",
      requestId ?? ""
    );
    if (linkToCategoryPathId) {
      trackClick(
        IClickInfo.build({
          token: linkToCategoryPathId,
          request_id: requestId,
          type: IClickInfo.Type.CATEGORY,
          container_name: eventName,
        })
      );
    }
  };
  if (hasShowroomApparelC2TitleBannerDismissed) {
    return null;
  }

  return (
    <div className={className} ref={ref}>
      <Wrapper $hideMarginBottom={isLoggedOutPage}>
        <LeftWrapper ref={measuredRef}>
          <Typography variant="paragraphSansRegular" color={Core.text.inverse}>
            {layoutElement.description_text}
          </Typography>
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
            color={Core.text.inverse}
          >
            {layoutElement.main_text}
          </Typography>
          <Spacer size={{ mobileAndAbove: "2x", tabletAndAbove: "1x" }} />
          <Row align="center">
            <StyledButton
              variant="tertiary"
              linkTo={layoutElement.cta_link}
              onClick={onClick}
            >
              {layoutElement.cta_text}
            </StyledButton>
            <Spacer size="1x" />
            <Arrow direction="right" color={Core.icon.inverse} />
          </Row>
        </LeftWrapper>
        <ImageContainer
          images={layoutElement.images}
          isMobile={isMobile}
          expectedContainerHeight={expectedContainerHeight}
        />

        {isLoggedOutPage ? null : (
          <CloseWrapper onClick={onClose}>
            <Close color={Core.icon.inverse} />
          </CloseWrapper>
        )}
      </Wrapper>
    </div>
  );
};

const ImageContainer: React.FC<{
  images: IImage[];
  isMobile: boolean;
  expectedContainerHeight: number;
}> = ({ images, isMobile, expectedContainerHeight }) => {
  if (isMobile || images.length !== 4) {
    return null;
  }

  return (
    <>
      <ImageWrapper data-test-id="image-wrapper">
        {images[0] === undefined ? null : (
          <SingleImage
            image={images[0]}
            imageRatio={LEFT_IMAGE.width / LEFT_IMAGE.height}
            imageHeight={expectedContainerHeight - IMAGE_GAP * 2}
            key="showroom-banner-image-left"
          />
        )}
        <ImageColumnWrapper>
          {images.slice(1, 3).map((image, index) => (
            <SingleImage
              image={image}
              imageRatio={1}
              imageHeight={(expectedContainerHeight - IMAGE_GAP * 3) / 2}
              key={`showroom-banner-image-middle-${index}`}
            />
          ))}
        </ImageColumnWrapper>
        {images[3] === undefined ? null : (
          <SingleImage
            image={images[3]}
            imageRatio={RIGHT_IMAGE.width / RIGHT_IMAGE.height}
            imageHeight={expectedContainerHeight - IMAGE_GAP * 2}
            key="showroom-banner-image-right"
          />
        )}
      </ImageWrapper>
      <DarkOverlay />
    </>
  );
};

const Wrapper = styled(Row).attrs({
  align: "center",
  justify: "space-between",
})<{ $hideMarginBottom?: boolean }>`
  background-color: ${Core.surface.enabled};
  width: 100%;
  position: relative;
  overflow: hidden;
  ${({ $hideMarginBottom }) =>
    $hideMarginBottom
      ? ""
      : css`
          margin-bottom: ${getSpacing("4x")};
        `}
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x", 40, "3x", "5x")};
    ${({ $hideMarginBottom }) =>
      $hideMarginBottom
        ? ""
        : css`
            margin-bottom: ${getSpacing("2x")};
          `}
  }
`;

const LeftWrapper = styled(Column).attrs({ justify: "center" })`
  width: 100%;
  min-height: 200px;
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(26, 32)};
    width: fit-content;
  }
`;

const ImageWrapper = styled(Row)`
  padding: ${getSpacing(IMAGE_GAP, 0)};
`;

const ImageColumnWrapper = styled(Column).attrs({ justify: "space-between" })`
  padding: ${getSpacing(0, IMAGE_GAP)};
`;

const DarkOverlay = styled.div`
  width: 238px;
  height: 100%;
  background: linear-gradient(
    270deg,
    ${Core.surface.enabled} 0%,
    rgba(0, 0, 0, 0) 88.01%
  );
  position: absolute;
  right: 0;
  top: 0;
  pointer-events: none;
`;

const StyledButton = styled(Button)`
  text-decoration-color: ${Core.text.inverse};
  color: ${Core.text.inverse};
  width: fit-content;
  &:hover,
  &:active,
  &:focus {
    text-decoration-color: ${Core.text.inverse};
    color: ${Core.text.inverse};
  }
  position: unset;

  &:before {
    content: " ";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
`;

const CloseWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: ${getSpacing("1x")};
  cursor: pointer;
  z-index: 2;
`;
